// Eloquent Clicks filter bar (snippets/ec-filter-bar.liquid).
// Filter links and the sort/price controls go through the theme's AJAX collection update
// (list.filter-grid.js listens for EVENTS.ajaxCollectionRender). Without JavaScript the
// links and the price form still work as normal page loads.
import { EVENTS } from 'util.events'

class EcFilterBar extends HTMLElement {
  // Panel to reopen after the AJAX update replaces the bar (keeps multi-select dropdowns open)
  static reopenPanelId = null

  connectedCallback() {
    this.abortController = new AbortController()
    const options = { signal: this.abortController.signal }

    this.addEventListener('click', this.onClick.bind(this), options)
    this.addEventListener('keydown', this.onKeydown.bind(this), options)
    this.addEventListener('submit', this.onSubmit.bind(this), options)
    this.addEventListener('change', this.onChange.bind(this), options)
    document.addEventListener('click', this.onDocumentClick.bind(this), options)

    if (window.ecFocusSheetButton) {
      window.ecFocusSheetButton = false
      const button = [...this.querySelectorAll('[data-ec-sheet-open]')].find((el) => el.offsetParent !== null)
      if (button) requestAnimationFrame(() => button.focus())
    }

    if (EcFilterBar.reopenPanelId) {
      const toggle = this.querySelector(`[data-ec-dropdown-toggle][aria-controls="${EcFilterBar.reopenPanelId}"]`)
      EcFilterBar.reopenPanelId = null
      if (toggle) this.open(toggle)
    }
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  onClick(evt) {
    // "Filters" (mobile) and "All filters" (desktop) open the filter sheet
    const sheetButton = evt.target.closest('[data-ec-sheet-open]')
    if (sheetButton) {
      evt.preventDefault()
      this.closeAll()
      const dialog = document.getElementById(sheetButton.dataset.ecSheetOpen)
      dialog?.closest('ec-filter-sheet')?.open(sheetButton)
      return
    }

    const toggle = evt.target.closest('[data-ec-dropdown-toggle]')
    if (toggle) {
      evt.preventDefault()
      if (toggle.getAttribute('aria-expanded') === 'true') {
        this.closeAll()
      } else {
        this.closeAll()
        this.open(toggle)
      }
      return
    }

    const link = evt.target.closest('a[data-ec-filter-link]')
    if (!link) return
    evt.preventDefault()

    let url
    if (link.dataset.ecSort) {
      url = new URL(window.location.href)
      url.searchParams.set('sort_by', link.dataset.ecSort)
      url.searchParams.delete('page')
      EcFilterBar.reopenPanelId = null
    } else {
      url = new URL(link.href, window.location.href)
      // A choice inside a filter dropdown keeps that dropdown open for the next pick
      const panel = link.closest('.ec-dropdown__panel')
      EcFilterBar.reopenPanelId = panel ? panel.id : null
    }

    this.closeAll()
    this.render(url)
  }

  onKeydown(evt) {
    if (evt.key !== 'Escape') return
    const openToggle = this.querySelector('[data-ec-dropdown-toggle][aria-expanded="true"]')
    if (openToggle) {
      this.closeAll()
      openToggle.focus()
    }
  }

  onDocumentClick(evt) {
    if (!this.contains(evt.target)) this.closeAll()
  }

  // Price range: apply when a field changes (or Enter submits the form)
  onChange(evt) {
    if (evt.target.closest('form[data-ec-price]')) this.applyPrice(evt.target.form)
  }

  onSubmit(evt) {
    const form = evt.target.closest('form[data-ec-price]')
    if (!form) return
    evt.preventDefault()
    this.applyPrice(form)
  }

  applyPrice(form) {
    const url = new URL(window.location.href)
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input.value === '') {
        url.searchParams.delete(input.name)
      } else {
        url.searchParams.set(input.name, input.value)
      }
    })
    url.searchParams.delete('page')
    const panel = form.closest('.ec-dropdown__panel')
    EcFilterBar.reopenPanelId = panel ? panel.id : null
    this.render(url)
  }

  open(toggle) {
    const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`)
    if (!panel) return
    toggle.setAttribute('aria-expanded', 'true')
    panel.hidden = false
  }

  closeAll() {
    this.querySelectorAll('[data-ec-dropdown-toggle][aria-expanded="true"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false')
      const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`)
      if (panel) panel.hidden = true
    })
  }

  render(url) {
    this.dispatchEvent(new CustomEvent(EVENTS.ajaxCollectionRender, { detail: { newUrl: url }, bubbles: true }))
  }
}

if (!customElements.get('ec-filter-bar')) {
  customElements.define('ec-filter-bar', EcFilterBar)
}
